import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Zap, Star, Shield, TrendingUp,
  Layers, ChevronDown, CheckCircle, Users,
  Cpu, DollarSign, Sparkles, Bell,
} from 'lucide-react';
import Logo from '../components/ui/Logo';
import ParticleField from '../components/ui/ParticleField';

// Animated counter
function Counter({ end, duration = 2000, prefix = '', suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0;
        const step = end / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

// Fake ticker tape
function TickerTape() {
  const tickers = [
    { t: 'SPY',  p: 587.42, c: +1.23 }, { t: 'NVDA', p: 142.67, c: +3.87 },
    { t: 'TSLA', p: 271.05, c: -2.14 }, { t: 'META', p: 612.33, c: +2.01 },
    { t: 'QQQ',  p: 498.91, c: -0.45 }, { t: 'AAPL', p: 228.34, c: +0.92 },
    { t: 'MSFT', p: 442.18, c: +0.61 }, { t: 'AMD',  p: 116.42, c: +2.78 },
    { t: 'GOOGL',p: 198.05, c: -0.32 }, { t: 'AMZN', p: 235.91, c: +1.05 },
    { t: 'COIN', p: 267.55, c: +5.23 }, { t: 'PLTR', p:  82.30, c: +1.88 },
  ];
  return (
    <div style={{
      overflow: 'hidden',
      borderTop: '1px solid var(--border-subtle)',
      borderBottom: '1px solid var(--border-subtle)',
      padding: '10px 0',
      background: 'var(--bg-card)',
    }}>
      <div style={{ display: 'flex', gap: 32, animation: 'scroll-ticker 30s linear infinite', whiteSpace: 'nowrap' }}>
        {[...tickers, ...tickers].map((t, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }} className="mono">
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{t.t}</span>
            <span style={{ color: 'var(--text-secondary)' }}>${t.p.toFixed(2)}</span>
            <span style={{ color: t.c >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
              {t.c >= 0 ? '+' : ''}{t.c.toFixed(2)}%
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Animated mock dashboard preview
function DashboardPreview() {
  const [activeBar, setActiveBar] = useState(3);
  useEffect(() => {
    const iv = setInterval(() => setActiveBar(p => (p + 1) % 7), 1500);
    return () => clearInterval(iv);
  }, []);
  const bars = [65, 42, 78, 55, 88, 35, 72];
  return (
    <div className="glass animate-float" style={{
      borderRadius: 'var(--r-xl)',
      border: '1px solid rgba(91,106,240,0.18)',
      padding: '20px', boxShadow: 'var(--shadow-modal)', maxWidth: 480, width: '100%',
    }}>
      {/* Fake title bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--red)' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--amber)' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)' }} />
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-faint)' }}>Stokora — Dashboard</span>
      </div>
      {/* Fake index cards */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { n: 'S&P 500', v: '587.42', c: '+1.23%', up: true },
          { n: 'NASDAQ', v: '498.91', c: '-0.45%', up: false },
          { n: 'Russell', v: '221.34', c: '+0.87%', up: true },
        ].map(idx => (
          <div key={idx.n} style={{
            flex: 1, background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: '10px 12px',
            border: `1px solid ${idx.up ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)'}`,
          }}>
            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{idx.n}</div>
            <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>${idx.v}</div>
            <div className="mono" style={{ fontSize: 10, color: idx.up ? 'var(--green)' : 'var(--red)' }}>{idx.c}</div>
          </div>
        ))}
      </div>
      {/* Fake bar chart */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 80, marginBottom: 12 }}>
        {bars.map((h, i) => (
          <div key={i} style={{
            flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0',
            transition: `all var(--dur-slow) var(--ease-out)`,
            background: i === activeBar ? 'var(--accent)' : 'var(--accent-dim)',
            boxShadow: i === activeBar ? '0 0 12px var(--accent-glow)' : 'none',
          }} />
        ))}
      </div>
      {/* Fake signal row */}
      <div style={{
        background: 'var(--bg-elevated)', borderRadius: 'var(--r-sm)', padding: '10px 12px',
        border: '1px solid rgba(34,197,94,0.18)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>NVDA</span>
            <span style={{ fontSize: 9, marginLeft: 6, padding: '1px 6px', borderRadius: 'var(--r-xs)', background: 'var(--green-dim)', color: 'var(--green)', fontWeight: 600 }}>LONG</span>
          </div>
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>87% confidence</span>
        </div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>RSI oversold (28.4) · MACD bullish · Volume surge</div>
      </div>
    </div>
  );
}

// Animated signal preview
function SignalPreview() {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setPulse(p => !p), 2000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="glass" style={{
      borderRadius: 'var(--r-xl)',
      border: '1px solid rgba(245,158,11,0.18)',
      padding: '20px', boxShadow: 'var(--shadow-modal)', maxWidth: 380, width: '100%',
    }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--red)' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--amber)' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)' }} />
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-faint)' }}>Trade Signals</span>
      </div>
      {[
        { t: 'AAPL', d: 'LONG', c: 82, r: 'RSI oversold (29.1)', tone: 'green' },
        { t: 'TSLA', d: 'SHORT', c: 74, r: 'Upper Bollinger · MACD bearish', tone: 'red' },
        { t: 'SPY', d: 'LONG', c: 91, r: 'Volume surge (2.3x) · SMA cross', tone: 'green' },
      ].map((s, i) => {
        const col = s.tone === 'green' ? 'var(--green)' : 'var(--red)';
        const tint = s.tone === 'green' ? 'var(--green-dim)' : 'var(--red-dim)';
        const border = s.tone === 'green' ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)';
        const glow = s.tone === 'green' ? 'rgba(34,197,94,0.20)' : 'rgba(239,68,68,0.20)';
        return (
        <div key={s.t} style={{
          background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: '12px', marginBottom: 8,
          border: `1px solid ${border}`,
          transition: `all var(--dur-slow) var(--ease-out)`,
          transform: pulse && i === 0 ? 'scale(1.02)' : 'scale(1)',
          boxShadow: pulse && i === 0 ? `0 0 20px ${glow}` : 'none',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{s.t}</span>
              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 'var(--r-xs)', background: tint, color: col, fontWeight: 600 }}>{s.d}</span>
            </div>
            <span style={{ fontSize: 11, color: s.c > 80 ? 'var(--green)' : 'var(--amber)', fontWeight: 600 }}>{s.c}%</span>
          </div>
          <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-card)', marginBottom: 6 }}>
            <div style={{
              height: '100%', borderRadius: 2, width: `${s.c}%`,
              background: s.c > 80 ? 'var(--grad-success)' : 'var(--grad-amber)',
              transition: 'width 1s var(--ease-out)',
            }} />
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{s.r}</div>
        </div>
        );
      })}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', overflow: 'hidden' }}>

      {/* ── Sticky Nav ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(5,8,15,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border-subtle)' : '1px solid transparent',
        transition: `all var(--dur-base) var(--ease-out)`,
      }}>
        <Logo size={22} onClick={() => navigate('/')} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/login')} style={{
            padding: '9px 20px', borderRadius: 'var(--r-md)',
            border: '1px solid var(--border-strong)',
            background: 'var(--accent-dim)', color: 'var(--accent-soft)',
            fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
            transition: `all var(--dur-fast) var(--ease-out)`,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(91,106,240,0.18)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent-dim)'; }}
          >Sign In</button>
          <button onClick={() => navigate('/login')} style={{
            padding: '9px 20px', borderRadius: 'var(--r-md)', border: 'none',
            background: 'var(--grad-accent)', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 4px 14px rgba(91,106,240,0.32)',
            transition: `all var(--dur-fast) var(--ease-out)`,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(91,106,240,0.45)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(91,106,240,0.32)'; }}
          >Get Started</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '100px 24px 60px', overflow: 'hidden',
      }}>
        {/* Interactive particle network — cursor repels nearby points and
            lights up the local neighbourhood. Drifting candle silhouettes
            tucked behind for on-brand quiet motion. */}
        <ParticleField />

        {/* Soft brand-coloured glow orbs to add depth on top of the canvas.
            pointerEvents: none so they don't swallow particle hover. */}
        <div style={{
          position: 'absolute', top: '12%', left: '8%',
          width: 520, height: 520, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(91,106,240,0.18) 0%, transparent 70%)',
          filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', bottom: '14%', right: '6%',
          width: 460, height: 460, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,197,94,0.13) 0%, transparent 70%)',
          filter: 'blur(70px)', pointerEvents: 'none', zIndex: 0,
        }} />

        <div style={{ maxWidth: 1200, width: '100%', display: 'flex', alignItems: 'center', gap: 60, flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
          {/* Left copy */}
          <div className="animate-fade-in" style={{ flex: '1 1 400px', maxWidth: 560 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 'var(--r-pill)',
              background: 'var(--accent-dim)',
              border: '1px solid var(--border-strong)',
              marginBottom: 24, fontSize: 12, color: 'var(--accent-soft)', fontWeight: 600,
              letterSpacing: '0.3px',
            }}>
              <Zap size={12} /> 🇺🇸 NYSE + NASDAQ Full Market Scan
            </div>
            <h1 style={{ fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 800, lineHeight: 1.1, margin: '0 0 20px 0', letterSpacing: '-1.5px' }}>
              Trade Smarter<br />
              <span style={{
                background: 'linear-gradient(135deg, #6366f1, #a78bfa, #22c55e)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                with Real Signals
              </span>
            </h1>
            <p style={{ fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 24px 0', maxWidth: 480 }}>
              Stokora — from "stok" (stock) and Latin "ora" (observe), a name that literally means "observe stocks," reflecting our mission to deliver vigilant market insights.
            </p>
            <p style={{ fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 32px 0', maxWidth: 480 }}>
              Real-time signals from a full NYSE + NASDAQ market scan (8,000+ tickers) via Polygon.io. RSI, MACD, Bollinger Bands and 10 other indicators — confidence-scored with options plays, paper trading, and a $10,000 USD paper portfolio.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/login')} style={{
                padding: '14px 28px', borderRadius: 'var(--r-md)', border: 'none',
                background: 'var(--grad-accent)', color: '#fff',
                fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: '0 6px 25px rgba(91,106,240,0.36)',
                transition: `all var(--dur-fast) var(--ease-out)`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(91,106,240,0.48)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 25px rgba(91,106,240,0.36)'; }}
              >Start Trading <ArrowRight size={18} /></button>
              <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} style={{
                padding: '14px 28px', borderRadius: 'var(--r-md)',
                border: '1px solid var(--border-strong)',
                background: 'var(--accent-dim)', color: 'var(--accent-soft)',
                fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                transition: `all var(--dur-fast) var(--ease-out)`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(91,106,240,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent-dim)'; }}
              >See Features <ChevronDown size={18} /></button>
            </div>
            {/* Stats */}
            <div style={{ display: 'flex', gap: 32, marginTop: 40, flexWrap: 'wrap' }}>
              {[
                { n: <Counter end={8000} suffix="+" />, l: 'Tickers Scanned Daily' },
                { n: <Counter end={13} />,              l: 'Independent Indicators' },
                { n: '$10K',                            l: 'Paper-Trade Starting Balance' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="mono" style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>{s.n}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right preview */}
          <div className="animate-slide-up" style={{ flex: '1 1 380px', display: 'flex', justifyContent: 'center' }}>
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* ── Ticker Tape ── */}
      <TickerTape />

      {/* ── Features ── */}
      <section id="features" style={{ padding: '80px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-1px' }}>
            Everything You Need to <span style={{
              background: 'var(--grad-accent)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Win</span>
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-muted)', maxWidth: 600, margin: '0 auto' }}>
            Institutional-grade tools at your fingertips. Real data, real signals, real edge.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {[
            { icon: Zap, title: 'Full Market Scan', desc: 'Every NYSE + NASDAQ-listed stock is scanned daily via Polygon\'s grouped-daily endpoint — 8,000+ tickers in a single API call. The top 50 by momentum get full technical analysis end-to-end.', color: 'var(--amber)', tint: 'var(--amber-dim)' },
            { icon: TrendingUp, title: 'Live Market Data', desc: 'Polygon.io powers everything: live quotes, daily history, today\'s price bars, the full options chain with Greeks, the VIX index, company news, and the underlying bars used to compute every indicator locally on our server.', color: 'var(--green)', tint: 'var(--green-dim)' },
            { icon: Layers, title: 'Options Suggestions', desc: 'Auto-generated options plays from the real Polygon options chain. Best contract selected near ATM. Real bid/ask, Greeks (Δ Γ Θ V), IV-based strategy (sell premium / debit spread / buy options).', color: 'var(--purple)', tint: 'var(--purple-dim)' },
            { icon: DollarSign, title: 'Paper Trading', desc: 'Every account starts with $10,000 USD. Paper trade stocks and options with real P&L tracking. Separate tabs for stocks vs options. Realized + unrealized P&L.', color: 'var(--green)', tint: 'var(--green-dim)' },
            { icon: Star, title: 'Personal Watchlist', desc: 'Search any NYSE + NASDAQ ticker via Polygon symbol search and add it to your watchlist. Each card shows live prices, signal direction, and a one-click trade button.', color: 'var(--amber)', tint: 'var(--amber-dim)' },
            { icon: BarChart3, title: 'P&L Probability', desc: 'Bell-curve probability distributions for 5-day returns based on implied volatility. See expected move, probability of profit, and the 68% price range. Updated every time you open a security.', color: 'var(--accent)', tint: 'var(--accent-dim)' },
            { icon: Shield, title: 'Secure Access', desc: 'Bcrypt 12-round hashing, 15-min JWT access tokens with 7-day refresh rotation, admin approval workflow, rate limiting (20 auth/15min, 10 trades/min). API keys server-side only — never in the browser.', color: '#0ea5e9', tint: 'rgba(14,165,233,0.10)' },
            { icon: Cpu, title: 'Real Options Chain', desc: 'Options data sourced live from Polygon\'s options chain. Real premiums, IV, and open interest. Position valuation uses live Polygon bid/ask midpoint, falling back to entry price — no model-based estimates.', color: '#f97316', tint: 'rgba(249,115,22,0.10)' },
            { icon: Sparkles, title: 'AI Signal Analysis', desc: 'One click sends all loaded signals to Groq\'s LLaMA model for a bulk AI evaluation. Each signal card shows an AI confidence adjustment (±15) with a reasoning note. A summary banner gives the AI\'s overall market read. Powered by Groq\'s free tier — no extra cost.', color: 'var(--purple)', tint: 'var(--purple-dim)' },
            { icon: Bell, title: 'Smart Email Alerts', desc: 'Daily digest after market open with the top 5 backtested signals personalised to your Watchlist. Exit alerts fire once when an open trade\'s Signal Health drops to CONSIDER EXIT — showing entry price, current price, unrealised P&L, and indicator reasons.', color: 'var(--green)', tint: 'var(--green-dim)' },
          ].map((f, i) => (
            <div key={i} style={{
              background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '26px 22px',
              border: '1px solid var(--border-subtle)',
              transition: `all var(--dur-base) var(--ease-out)`,
              cursor: 'default',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lift)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 'var(--r-md)', marginBottom: 16,
                background: f.tint, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${f.color}25`,
              }}><f.icon size={22} color={f.color} /></div>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: 'var(--text-primary)', letterSpacing: '-0.2px' }}>{f.title}</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Signal Preview Section ── */}
      <section style={{ padding: '60px 24px 80px', background: 'var(--bg-surface)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 60, flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ flex: '1 1 340px', display: 'flex', justifyContent: 'center' }}>
            <SignalPreview />
          </div>
          <div style={{ flex: '1 1 380px', maxWidth: 500 }}>
            <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, margin: '0 0 16px', letterSpacing: '-0.5px' }}>
              Confidence-Scored Trade Signals
            </h2>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 24px' }}>
              Every listed NYSE + NASDAQ stock is scanned in one API call, then the top candidates are scored across 13 independent indicators. Higher confidence = more indicators aligned.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { icon: CheckCircle, text: '8,000+ tickers scanned daily, top 50 get full technicals' },
                { icon: CheckCircle, text: 'Decorrelated cluster scoring across 6 indicator families' },
                { icon: CheckCircle, text: 'Entry, target, and stop-loss derived from ATR + IV' },
                { icon: CheckCircle, text: 'Options play with Greeks (Δ Γ Θ V), breakeven, IV strategy' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-primary)' }}>
                  <item.icon size={16} color="var(--green)" style={{ flexShrink: 0 }} />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: '80px 24px', maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, textAlign: 'center', margin: '0 0 48px', letterSpacing: '-0.5px' }}>
          Get Started in <span style={{
            background: 'var(--grad-success)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>3 Steps</span>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { step: '1', title: 'Create Account', desc: 'Sign up with your name, email, and password. Accounts require admin approval for security.', icon: Users },
            { step: '2', title: 'Get Approved', desc: 'Once approved by the admin, you get full access to the dashboard, signals, and watchlist.', icon: Shield },
            { step: '3', title: 'Start Trading', desc: 'Browse live signals, add favorites to your watchlist, analyze technicals, and execute with confidence.', icon: TrendingUp },
          ].map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 20, alignItems: 'flex-start',
              padding: '24px 0',
              borderBottom: i < 2 ? '1px solid var(--border-subtle)' : 'none',
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 'var(--r-lg)', flexShrink: 0,
                background: 'var(--grad-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 800, color: '#fff',
                boxShadow: '0 4px 14px rgba(91,106,240,0.32)',
              }}>{s.step}</div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px', color: 'var(--text-primary)' }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{
          maxWidth: 700, margin: '0 auto', padding: '48px 32px', borderRadius: 'var(--r-2xl)',
          background: 'linear-gradient(135deg, rgba(91,106,240,0.12), rgba(139,92,246,0.08))',
          border: '1px solid var(--border-strong)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
        }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>
            Ready to Trade?
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', margin: '0 0 28px', maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
            Join Stokora and get access to full-market NYSE + NASDAQ signals, a $10,000 USD paper portfolio, and institutional-grade technical analysis.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/login')} style={{
              padding: '14px 32px', borderRadius: 'var(--r-md)', border: 'none',
              background: 'var(--grad-accent)', color: '#fff',
              fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 6px 25px rgba(91,106,240,0.36)',
              transition: `all var(--dur-fast) var(--ease-out)`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(91,106,240,0.48)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 25px rgba(91,106,240,0.36)'; }}
            >Create Free Account <ArrowRight size={18} /></button>
            <button onClick={() => navigate('/login')} style={{
              padding: '14px 32px', borderRadius: 'var(--r-md)',
              border: '1px solid var(--border-strong)',
              background: 'transparent', color: 'var(--accent-soft)',
              fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              transition: `all var(--dur-fast) var(--ease-out)`,
            }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-dim)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >Sign In</button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        padding: '24px',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        maxWidth: 1200, margin: '0 auto', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo size={14} animated={false} />
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>© 2026</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>
          🇺🇸 NYSE + NASDAQ · Not financial advice · Polygon.io · Groq AI
        </div>
      </footer>

      {/* ── Animations ── */}
      <style>{`
        @keyframes scroll-ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
